/**
 * Load More — search (Relevanssi) + regular post/archive listings
 *
 * Features (all opt-in via data-attributes on the `.load-more` button):
 *   - data-search-query      → routes the request through `wp_ajax_search_load_more`
 *                              which runs the query through Relevanssi
 *   - data-infinite-scroll   → "true" enables IntersectionObserver-driven auto-load
 *   - data-preserve-state    → "true" saves rendered HTML + scroll position to
 *                              sessionStorage so back-navigation restores the list
 *   - data-instance-id       → identifies the component for multi-instance pages
 *                              (state keys + container scoping)
 *
 * Without any opt-in attributes the button behaves like a plain click-to-load-more.
 */

// ----------------------------------------------------------------------------
// Optional WooCommerce hook — no-op in lwlightning (kept as an extension point)
// ----------------------------------------------------------------------------

const addVariationsToProducts = () => {
    // Lightning theme has no WooCommerce variations helper.
    // Leave as no-op so the rest of the module stays portable.
}

// Track loading state per instance to prevent multiple simultaneous requests
const loadingStates = new Map()

// Storage key prefix for sessionStorage entries
const STORAGE_KEY_PREFIX = 'list_state_'

// ============================================================================
// Instance & container helpers
// ============================================================================

const getInstanceId = (button) => button?.dataset?.instanceId || null

const isStatePreservationEnabled = (button) => button?.dataset?.preserveState === 'true'

const getStateKey = (button) => {
    const instanceId = getInstanceId(button)

    if (button?.dataset?.searchQuery) {
        return `search:${button.dataset.searchQuery}`
    }

    // Instance-scoped components (e.g. pc-post-listing) own their filter
    // state internally, so the key must stay stable across filter changes.
    // The active cat-id is persisted inside the state payload instead.
    if (instanceId) {
        return `instance:${instanceId}:${window.location.pathname}`
    }

    if (button?.dataset?.catId) {
        const postType = button.dataset.postType || 'post'
        return `archive:${postType}:${button.dataset.catId}`
    }

    const postType = button?.dataset?.postType || 'post'
    return `listing:${postType}:${window.location.pathname}`
}

const getStorageKey = (stateKey) => STORAGE_KEY_PREFIX + stateKey

const getPostContainer = (button) => {
    const instanceId = getInstanceId(button)

    if (instanceId) {
        // Look for a wrapper element with the same instance-id — but skip
        // the button itself, otherwise we'd inject loaded posts INSIDE the
        // button. Falls through to the .container-based lookup below when
        // no wrapper has been tagged.
        const wrappers = document.querySelectorAll(`[data-instance-id="${instanceId}"]`)
        for (const wrapper of wrappers) {
            if (wrapper === button) continue
            const postListing = wrapper.querySelector('.post-listing')
            if (postListing) return postListing
            return wrapper
        }
    }

    return button?.closest('.container')?.querySelector('.post-listing')
}

const isInstanceLoading = (button) => {
    const instanceId = getInstanceId(button) || 'global'
    return loadingStates.get(instanceId) || false
}

const setInstanceLoading = (button, loading) => {
    const instanceId = getInstanceId(button) || 'global'
    loadingStates.set(instanceId, loading)
}

// ============================================================================
// State preservation (sessionStorage)
// ============================================================================

const getInstanceWrapper = (button) => {
    const instanceId = getInstanceId(button)
    if (!instanceId) return null
    return document.querySelector(`[data-instance-id="${instanceId}"]`)
}

const applyActiveCatFilter = (button, catId) => {
    const wrapper = getInstanceWrapper(button)
    if (!wrapper) return

    const catFilters = wrapper.querySelectorAll('.cat-filter')
    if (!catFilters.length) return

    catFilters.forEach((filter) => {
        filter.classList.toggle('active', filter.dataset.value === catId)
    })
}

const saveState = (button, container) => {
    if (!isStatePreservationEnabled(button)) return

    const stateKey = getStateKey(button)
    if (!stateKey || !container) return

    const state = {
        html: container.innerHTML,
        paged: button?.dataset.paged || '1',
        maxPages: button?.dataset.maxPages || '',
        catId: button?.dataset.catId || '',
        scrollY: window.scrollY,
        timestamp: Date.now(),
    }

    try {
        sessionStorage.setItem(getStorageKey(stateKey), JSON.stringify(state))
    } catch (e) {
        console.warn('Could not save list state:', e)
    }
}

const restoreState = (button, container) => {
    if (!isStatePreservationEnabled(button)) return false

    const stateKey = getStateKey(button)
    if (!stateKey || !container) return false

    try {
        const stored = sessionStorage.getItem(getStorageKey(stateKey))
        if (!stored) return false

        const state = JSON.parse(stored)

        // Drop states older than 30 minutes
        if (Date.now() - state.timestamp > 30 * 60 * 1000) {
            sessionStorage.removeItem(getStorageKey(stateKey))
            return false
        }

        container.innerHTML = state.html

        if (button) {
            button.dataset.paged = state.paged
            if (state.maxPages) {
                button.dataset.maxPages = state.maxPages
            }
            if (typeof state.catId === 'string') {
                button.dataset.catId = state.catId
                applyActiveCatFilter(button, state.catId)
            }
        }

        requestAnimationFrame(() => {
            window.scrollTo(0, state.scrollY)
        })

        addVariationsToProducts()

        return true
    } catch (e) {
        console.warn('Could not restore list state:', e)
        return false
    }
}

const clearOldStates = () => {
    try {
        const keysToRemove = []
        for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i)
            if (key?.startsWith(STORAGE_KEY_PREFIX)) {
                const stored = sessionStorage.getItem(key)
                if (stored) {
                    const state = JSON.parse(stored)
                    if (Date.now() - state.timestamp > 30 * 60 * 1000) {
                        keysToRemove.push(key)
                    }
                }
            }
        }
        keysToRemove.forEach((key) => sessionStorage.removeItem(key))
    } catch (e) {
        // Fail silently
    }
}

const setupStatePreservation = () => {
    const loadMoreButtons = document.querySelectorAll('.load-more[data-preserve-state="true"]')

    loadMoreButtons.forEach((button) => {
        const container = getPostContainer(button)
        if (!container) return

        // Save state when the user clicks a result link inside the container
        container.addEventListener('click', (e) => {
            const link = e.target.closest('a')
            if (link && !link.closest('.load-more')) {
                saveState(button, container)
            }
        })
    })

    window.addEventListener('pagehide', () => {
        loadMoreButtons.forEach((button) => {
            const container = getPostContainer(button)
            if (container) {
                saveState(button, container)
            }
        })
    })
}

const tryRestoreStates = () => {
    const loadMoreButtons = document.querySelectorAll('.load-more[data-preserve-state="true"]')

    loadMoreButtons.forEach((button) => {
        const container = getPostContainer(button)
        if (!container) return

        const restored = restoreState(button, container)

        if (restored) {
            const maxPages = parseInt(button.dataset.maxPages || '999', 10)
            const currentPage = parseInt(button.dataset.paged || '1', 10)
            if (currentPage >= maxPages) {
                button.style.display = 'none'
            }
        }
    })
}

// ============================================================================
// Core helpers
// ============================================================================

const getLoadMoreButton = (target) => target?.closest('.load-more')

const setButtonState = (button, { loading, disabled, text, hide }) => {
    if (typeof loading !== 'undefined') {
        button.classList.toggle('loading', loading)
        setInstanceLoading(button, loading)
    }

    if (typeof disabled !== 'undefined') {
        if (disabled) {
            button.setAttribute('disabled', 'disabled')
        } else {
            button.removeAttribute('disabled')
        }
    }

    if (typeof text !== 'undefined') {
        const label = button.querySelector('.load-more-text')
        if (label) {
            label.textContent = text
        }
    }

    if (typeof hide !== 'undefined') {
        button.style.display = hide ? 'none' : ''
    }
}

const appendPosts = (container, posts = []) => {
    posts.forEach((post) => {
        container.insertAdjacentHTML('beforeend', post)
    })
}

const isSearchLoadMore = (button) => Boolean(button.dataset.searchQuery && button.dataset.searchQuery.length > 0)

// ============================================================================
// Search load more (Relevanssi)
// ============================================================================

const buildSearchRequestData = (button) => {
    const data = new FormData()
    const { dataset } = button

    const currentPage = parseInt(dataset.paged || '1', 10)
    const nextPage = currentPage + 1

    data.append('action', 'search_load_more')
    data.append('security', liGlobal.loadMore)
    data.append('search_query', dataset.searchQuery)
    data.append('paged', nextPage)

    if (dataset.postsPerPage) {
        data.append('posts_per_page', dataset.postsPerPage)
    }

    if (dataset.postType) {
        data.append('post_type', dataset.postType)
    }

    return { data, nextPage }
}

const requestSearchLoadMore = async (formData) => {
    const response = await fetch(liGlobal.ajaxUrl, {
        method: 'POST',
        body: formData,
    })

    if (!response.ok) {
        throw new Error('Nätverksfel vid sökning')
    }

    const payload = await response.json()

    if (!payload.success) {
        throw new Error(payload.data?.message || 'Servern returnerade ett fel')
    }

    return payload.data
}

const handleSearchLoadMoreSuccess = (button, container, data, nextPage) => {
    appendPosts(container, data.posts)

    button.dataset.paged = nextPage.toString()
    button.dataset.maxPages = data.max_num_pages?.toString() || ''

    if (data.no_more_posts) {
        setButtonState(button, {
            loading: false,
            disabled: true,
            text: data.load_more_btn_text,
            hide: true,
        })
    } else {
        setButtonState(button, {
            loading: false,
            disabled: false,
            text: data.load_more_btn_text,
        })
    }

    if (data.posts && data.posts.length > 0) {
        addVariationsToProducts()
    }

    saveState(button, container)
}

const triggerSearchLoadMore = async (button) => {
    if (isInstanceLoading(button) || button.disabled) {
        return
    }

    const postContainer = getPostContainer(button)
    if (!postContainer) {
        return
    }

    setButtonState(button, { loading: true })

    const { data: formData, nextPage } = buildSearchRequestData(button)

    try {
        const data = await requestSearchLoadMore(formData)

        if (data.no_more_posts && !data.posts?.length) {
            setButtonState(button, {
                loading: false,
                disabled: true,
                text: data.load_more_btn_text,
                hide: true,
            })
            return
        }

        handleSearchLoadMoreSuccess(button, postContainer, data, nextPage)
    } catch (error) {
        console.error('Kunde inte ladda fler sökresultat', error)
        setButtonState(button, { loading: false, disabled: false })
    }
}

const loadSearchPosts = async (event) => {
    event.preventDefault()

    const loadMoreBtn = getLoadMoreButton(event.target)
    if (!loadMoreBtn) {
        return
    }

    await triggerSearchLoadMore(loadMoreBtn)
}

// ============================================================================
// Regular load more (non-search)
// ============================================================================

const collectExcludedPostIds = (container) => {
    if (!container) {
        return [...document.querySelectorAll('[data-post-id]')].map((post) => post.dataset.postId).filter(Boolean)
    }
    return [...container.querySelectorAll('[data-post-id]')].map((post) => post.dataset.postId).filter(Boolean)
}

const buildRequestData = (button, container) => {
    const data = new FormData()
    const { dataset } = button

    const currentPage = parseInt(dataset.paged || '1', 10)
    const nextPage = currentPage + 1

    if (dataset.catId) {
        data.append('cat_id', dataset.catId)
    }

    if (dataset.categoryIds) {
        data.append('category_ids', dataset.categoryIds)
    }

    if (dataset.categoryTaxonomy) {
        data.append('category_taxonomy', dataset.categoryTaxonomy)
    }

    if (dataset.productCategory) {
        data.append('product_id', dataset.productCategory)
    }

    if (dataset.productBrand) {
        data.append('product_brand', dataset.productBrand)
    }

    const excludedIds = collectExcludedPostIds(container)
    if (excludedIds.length) {
        data.append('exclude', excludedIds.join(','))
    }

    if (dataset.postType) {
        data.append('post_type', dataset.postType)
    }

    if (dataset.postsPerPage) {
        data.append('posts_per_page', dataset.postsPerPage)
    }

    if (dataset.orderby) {
        data.append('orderby', dataset.orderby)
    }

    if (dataset.order) {
        data.append('order', dataset.order)
    }

    data.append('action', 'load_more')
    data.append('security', liGlobal.loadMore)
    data.append('paged', nextPage)

    return { data, nextPage }
}

const requestLoadMorePosts = async (formData) => {
    const response = await fetch(liGlobal.ajaxUrl, {
        method: 'POST',
        body: formData,
    })

    if (!response.ok) {
        throw new Error('Nätverksfel vid Ladda fler-förfrågan')
    }

    const payload = await response.json()

    if (!payload.success) {
        throw new Error(payload.data?.message || 'Servern returnerade ett fel')
    }

    return payload.data
}

const handleLoadMoreSuccess = (button, container, data, nextPage) => {
    appendPosts(container, data.posts)

    button.dataset.paged = nextPage.toString()
    button.dataset.maxPages = data.max_num_pages?.toString() || ''

    if (data.no_more_posts) {
        setButtonState(button, {
            loading: false,
            disabled: true,
            text: data.load_more_btn_text,
            hide: true,
        })
    } else {
        setButtonState(button, {
            loading: false,
            disabled: false,
            text: data.load_more_btn_text,
        })
    }

    if (data.post_type === 'product') {
        addVariationsToProducts()
    }

    saveState(button, container)
}

const handleLoadMoreError = (button, error) => {
    console.error('Kunde inte ladda fler inlägg', error)
    setButtonState(button, { loading: false, disabled: false })
}

const triggerLoadMore = async (button) => {
    if (isInstanceLoading(button) || button.disabled) {
        return
    }

    const postContainer = getPostContainer(button)
    if (!postContainer) {
        return
    }

    setButtonState(button, { loading: true })

    const { data: formData, nextPage } = buildRequestData(button, postContainer)

    try {
        const data = await requestLoadMorePosts(formData)

        if (data.no_more_posts && !data.posts?.length) {
            setButtonState(button, {
                loading: false,
                disabled: true,
                text: data.load_more_btn_text,
                hide: true,
            })
            return
        }

        handleLoadMoreSuccess(button, postContainer, data, nextPage)
    } catch (error) {
        handleLoadMoreError(button, error)
    }
}

export const loadPosts = async (event) => {
    event.preventDefault()

    const loadMoreBtn = getLoadMoreButton(event.target)
    if (!loadMoreBtn) {
        return
    }

    await triggerLoadMore(loadMoreBtn)
}

// ============================================================================
// Infinite scroll (opt-in via data-infinite-scroll="true")
// ============================================================================

const initInfiniteScroll = () => {
    const loadMoreButtons = document.querySelectorAll('.load-more[data-infinite-scroll="true"]')

    if (!loadMoreButtons.length) {
        return
    }

    const observerOptions = {
        root: null,
        rootMargin: '400px',
        threshold: 0,
    }

    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                const button = entry.target

                if (button.disabled || isInstanceLoading(button)) {
                    if (button.disabled) {
                        observer.unobserve(button)
                    }
                    return
                }

                if (isSearchLoadMore(button)) {
                    triggerSearchLoadMore(button)
                } else {
                    triggerLoadMore(button)
                }
            }
        })
    }, observerOptions)

    loadMoreButtons.forEach((button) => {
        observer.observe(button)
    })
}

// ============================================================================
// Event listeners & initialization
// ============================================================================

document.addEventListener('click', (event) => {
    const loadMoreBtn = event.target.closest('.load-more')

    if (loadMoreBtn) {
        if (isSearchLoadMore(loadMoreBtn)) {
            loadSearchPosts(event)
        } else {
            loadPosts(event)
        }
    }
})

const init = () => {
    clearOldStates()
    tryRestoreStates()
    setupStatePreservation()
    initInfiniteScroll()
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init)
} else {
    init()
}

// Restore on bfcache page show (back/forward navigation)
window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
        tryRestoreStates()
    }
})
